/* ------------------------------------------------------------------
   bb-ugc.js
   Behaviour for the native UGC video slider (sections/bb-ugc-slider.liquid).

   - Muted preview playback only for tiles currently in view
   - Horizontal scroll-snap slider with arrows on desktop, swipe on touch
   - Full-screen lightbox with sound, controls, swipe/arrow navigation
   - Self-contained: no dependencies, shares nothing with bb-landing.js
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var instances = new Map();
  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- body scroll lock (shared across instances) ---------- */

  var lockCount = 0;
  var lockedScrollY = 0;

  function lockBody() {
    if (lockCount++ > 0) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('bbugc-lock');
    document.body.style.position = 'fixed';
    document.body.style.top = -lockedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockBody() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.classList.remove('bbugc-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, lockedScrollY);
  }

  /* ---------- helpers ---------- */

  function ensureSrc(video) {
    if (!video) return false;
    var src = video.getAttribute('data-src');
    if (src && !video.src) video.src = src;
    return Boolean(video.src);
  }

  function safePlay(video) {
    if (!video) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /* ---------- instance ---------- */

  function Slider(root) {
    this.root = root;
    this.track = root.querySelector('[data-bbugc-track]');
    this.prevBtn = root.querySelector('[data-bbugc-prev]');
    this.nextBtn = root.querySelector('[data-bbugc-next]');
    this.modal = root.querySelector('[data-bbugc-modal]');
    this.tiles = Array.prototype.slice.call(
      root.querySelectorAll('[data-bbugc-tile]')
    );
    this.previewMode = root.getAttribute('data-preview-mode') || 'rotate';
    this.rotateMs =
      (parseInt(root.getAttribute('data-rotate-seconds'), 10) || 5) * 1000;
    this.loopNav = root.getAttribute('data-loop-nav') === 'true';
    this.isOpen = false;
    this.activeIndex = 0;
    this.lastFocus = null;
    this.bound = [];

    // Preview playback state. At most ONE tile is ever unmuted-of-poster and
    // playing at a time — `spotlight` is the index of that tile, or -1.
    this.visibleFlags = [];
    this.spotlight = -1;
    this.rotateTimer = null;

    this.initSlider();
    this.initPreviews();
    this.initModal();
  }

  /* ----- slider ----- */

  Slider.prototype.on = function (el, type, fn, opts) {
    if (!el) return;
    el.addEventListener(type, fn, opts);
    this.bound.push([el, type, fn, opts]);
  };

  Slider.prototype.initSlider = function () {
    if (!this.track) return;
    var self = this;

    this.updateArrows = function () {
      if (!self.prevBtn || !self.nextBtn) return;
      var max = self.track.scrollWidth - self.track.clientWidth;
      var overflows = max > 2;
      var x = self.track.scrollLeft;
      self.prevBtn.hidden = !overflows || x <= 2;
      self.nextBtn.hidden = !overflows || x >= max - 2;
    };

    this.on(this.track, 'scroll', debounce(this.updateArrows, 80), {
      passive: true
    });

    this.on(this.prevBtn, 'click', function () {
      self.track.scrollBy({
        left: -Math.round(self.track.clientWidth * 0.85),
        behavior: 'smooth'
      });
    });

    this.on(this.nextBtn, 'click', function () {
      self.track.scrollBy({
        left: Math.round(self.track.clientWidth * 0.85),
        behavior: 'smooth'
      });
    });

    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(debounce(this.updateArrows, 120));
      this.ro.observe(this.track);
    } else {
      this.on(window, 'resize', debounce(this.updateArrows, 150));
    }

    this.updateArrows();
  };

  /* ----- tile previews ----- */

  Slider.prototype.previewsEnabled = function () {
    if (this.previewMode === 'none') return false;
    if (reduceMotion && reduceMotion.matches) return false;
    return true;
  };

  Slider.prototype.initPreviews = function () {
    if (!this.tiles.length) return;
    if (!this.previewsEnabled()) return;
    if (!('IntersectionObserver' in window)) return;
    var self = this;

    this.tiles.forEach(function (v, i) {
      self.visibleFlags[i] = false;
    });

    // Track which tiles are actually on screen; playback follows from that.
    this.tileObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = self.tiles.indexOf(entry.target);
          if (i > -1) self.visibleFlags[i] = entry.isIntersecting;
        });
        self.syncPlayback();
      },
      { threshold: 0.5 }
    );

    this.tiles.forEach(function (video) {
      self.tileObserver.observe(video);
    });

    // Background tabs shouldn't burn bandwidth on a rotation nobody sees.
    this.onVisibility = function () {
      self.syncPlayback();
    };
    this.on(document, 'visibilitychange', this.onVisibility);
  };

  // Single source of truth: decides whether anything should be playing,
  // and which one. Called on scroll-in/out, tab switch, and lightbox toggle.
  Slider.prototype.syncPlayback = function () {
    if (!this.previewsEnabled()) return;

    var offScreen = this.visibleFlags.indexOf(true) === -1;
    if (this.isOpen || document.hidden || offScreen) {
      this.stopRotation();
      this.clearSpotlight();
      return;
    }

    if (this.previewMode === 'first') {
      this.stopRotation();
      if (this.visibleFlags[0]) this.setSpotlight(0);
      else this.clearSpotlight();
      return;
    }

    // rotate: keep one tile lit, moving on every rotateMs
    if (this.spotlight === -1 || !this.visibleFlags[this.spotlight]) {
      this.setSpotlight(this.nextVisible(this.spotlight));
    }
    this.startRotation();
  };

  Slider.prototype.nextVisible = function (from) {
    var n = this.tiles.length;
    if (!n) return -1;
    var start = from < 0 ? -1 : from;
    for (var k = 1; k <= n; k++) {
      var i = (start + k + n) % n;
      if (this.visibleFlags[i]) return i;
    }
    return -1;
  };

  Slider.prototype.cardFor = function (video) {
    return video && video.closest ? video.closest('.bbugc__card') : null;
  };

  Slider.prototype.setSpotlight = function (i) {
    if (i === this.spotlight) return;
    this.clearSpotlight();
    if (i < 0 || !this.tiles[i]) return;

    this.spotlight = i;
    var video = this.tiles[i];
    var card = this.cardFor(video);
    if (card) card.classList.add('is-playing');
    if (ensureSrc(video)) safePlay(video);
  };

  Slider.prototype.clearSpotlight = function () {
    var video = this.tiles[this.spotlight];
    if (video) {
      if (!video.paused) video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
      var card = this.cardFor(video);
      if (card) card.classList.remove('is-playing');
    }
    this.spotlight = -1;
  };

  Slider.prototype.startRotation = function () {
    if (this.rotateTimer || this.previewMode !== 'rotate') return;
    var self = this;
    this.rotateTimer = setInterval(function () {
      if (self.isOpen || document.hidden) return;
      var next = self.nextVisible(self.spotlight);
      if (next === -1) {
        self.stopRotation();
        self.clearSpotlight();
        return;
      }
      self.setSpotlight(next);
    }, this.rotateMs);
  };

  Slider.prototype.stopRotation = function () {
    if (this.rotateTimer) {
      clearInterval(this.rotateTimer);
      this.rotateTimer = null;
    }
  };

  Slider.prototype.pauseTiles = function () {
    this.stopRotation();
    this.clearSpotlight();
  };

  Slider.prototype.resumeTiles = function () {
    this.syncPlayback();
  };

  /* ----- lightbox ----- */

  Slider.prototype.initModal = function () {
    if (!this.modal) return;
    var self = this;

    // Move the lightbox to <body> so no ancestor transform can trap it.
    document.body.appendChild(this.modal);

    this.modalTrack = this.modal.querySelector('[data-bbugc-modal-track]');
    this.modalSlides = Array.prototype.slice.call(
      this.modal.querySelectorAll('[data-bbugc-modal-slide]')
    );
    this.modalVideos = Array.prototype.slice.call(
      this.modal.querySelectorAll('[data-bbugc-modal-video]')
    );
    this.counter = this.modal.querySelector('[data-bbugc-counter]');
    this.modalPrev = this.modal.querySelector('[data-bbugc-modal-prev]');
    this.modalNext = this.modal.querySelector('[data-bbugc-modal-next]');

    this.root.querySelectorAll('[data-bbugc-open]').forEach(function (btn) {
      self.on(btn, 'click', function () {
        self.open(parseInt(btn.getAttribute('data-index'), 10) || 0, btn);
      });
    });

    this.modal.querySelectorAll('[data-bbugc-close]').forEach(function (btn) {
      self.on(btn, 'click', function () {
        self.close();
      });
    });

    this.on(this.modalPrev, 'click', function () {
      self.goTo(self.activeIndex - 1);
    });
    this.on(this.modalNext, 'click', function () {
      self.goTo(self.activeIndex + 1);
    });

    this.on(
      this.modalTrack,
      'scroll',
      debounce(function () {
        if (!self.isOpen || !self.modalTrack.clientWidth) return;
        var i = Math.round(
          self.modalTrack.scrollLeft / self.modalTrack.clientWidth
        );
        if (i !== self.activeIndex) self.setActive(i);
      }, 90),
      { passive: true }
    );

    this.onKeydown = function (e) {
      if (!self.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'ArrowRight') {
        self.goTo(self.activeIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        self.goTo(self.activeIndex - 1);
      }
    };
    this.on(document, 'keydown', this.onKeydown);

    this.onResize = debounce(function () {
      if (self.isOpen) self.jumpTo(self.activeIndex);
    }, 150);
    this.on(window, 'resize', this.onResize);
  };

  Slider.prototype.open = function (index, trigger) {
    if (!this.modal || this.isOpen) return;
    this.lastFocus = trigger || document.activeElement;
    this.isOpen = true;
    this.pauseTiles();
    lockBody();

    this.modal.hidden = false;
    // Force layout so clientWidth is real before positioning.
    void this.modal.offsetWidth;
    this.jumpTo(index);
    this.setActive(index);

    var self = this;
    requestAnimationFrame(function () {
      self.modal.classList.add('is-open');
      var closeBtn = self.modal.querySelector('.bbugc-modal__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    });
  };

  Slider.prototype.close = function () {
    if (!this.modal || !this.isOpen) return;
    this.isOpen = false;
    this.modal.classList.remove('is-open');

    this.modalVideos.forEach(function (v) {
      if (!v.paused) v.pause();
    });

    this.modal.hidden = true;
    unlockBody();
    this.resumeTiles();

    if (this.lastFocus && document.contains(this.lastFocus)) {
      this.lastFocus.focus({ preventScroll: true });
    }
    this.lastFocus = null;
  };

  Slider.prototype.jumpTo = function (index) {
    if (!this.modalTrack) return;
    var prev = this.modalTrack.style.scrollBehavior;
    this.modalTrack.style.scrollBehavior = 'auto';
    this.modalTrack.scrollLeft = index * this.modalTrack.clientWidth;
    this.modalTrack.style.scrollBehavior = prev || '';
  };

  Slider.prototype.goTo = function (index) {
    var count = this.modalSlides.length;
    if (!count || !this.modalTrack) return;

    var wrapped = false;
    if (index < 0) {
      if (!this.loopNav) return;
      index = count - 1;
      wrapped = true;
    } else if (index > count - 1) {
      if (!this.loopNav) return;
      index = 0;
      wrapped = true;
    }

    if (wrapped) {
      this.jumpTo(index);
    } else {
      this.modalTrack.scrollTo({
        left: index * this.modalTrack.clientWidth,
        behavior: reduceMotion && reduceMotion.matches ? 'auto' : 'smooth'
      });
    }
    this.setActive(index);
  };

  Slider.prototype.setActive = function (index) {
    var count = this.modalSlides.length;
    if (!count) return;
    index = Math.max(0, Math.min(count - 1, index));
    this.activeIndex = index;

    this.modalVideos.forEach(function (video, i) {
      if (i === index) {
        if (ensureSrc(video)) safePlay(video);
      } else if (!video.paused) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (e) {
          /* ignore */
        }
      }
    });

    if (this.counter) {
      this.counter.textContent = index + 1 + ' / ' + count;
    }
    if (this.modalPrev) {
      this.modalPrev.disabled = !this.loopNav && index === 0;
    }
    if (this.modalNext) {
      this.modalNext.disabled = !this.loopNav && index === count - 1;
    }
  };

  Slider.prototype.scrollTileIntoView = function (index) {
    var slide = this.root.querySelectorAll('.bbugc__slide')[index];
    if (slide && slide.scrollIntoView) {
      slide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  Slider.prototype.destroy = function () {
    this.close();
    this.stopRotation();
    this.clearSpotlight();
    if (this.tileObserver) this.tileObserver.disconnect();
    if (this.ro) this.ro.disconnect();
    this.bound.forEach(function (b) {
      b[0].removeEventListener(b[1], b[2], b[3]);
    });
    this.bound = [];
    if (this.modal && this.modal.parentNode === document.body) {
      document.body.removeChild(this.modal);
    }
  };

  /* ---------- boot ---------- */

  function keyFor(root) {
    var wrapper = root.closest('.shopify-section');
    return (wrapper && wrapper.id) || root.getAttribute('data-uid') || '';
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-bbugc]').forEach(function (root) {
      var key = keyFor(root);
      if (instances.has(key)) return;
      instances.set(key, new Slider(root));
    });
  }

  function destroy(key) {
    var inst = instances.get(key);
    if (inst) {
      inst.destroy();
      instances.delete(key);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor */

  document.addEventListener('shopify:section:load', function (e) {
    destroy(e.detail.sectionId ? 'shopify-section-' + e.detail.sectionId : '');
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-bbugc]');
    if (root) destroy(keyFor(root));
  });

  document.addEventListener('shopify:section:deselect', function (e) {
    var root = e.target.querySelector('[data-bbugc]');
    var inst = root && instances.get(keyFor(root));
    if (inst) inst.close();
  });

  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('[data-bbugc]');
    if (!root) return;
    var inst = instances.get(keyFor(root));
    if (!inst) return;
    var slides = Array.prototype.slice.call(
      root.querySelectorAll('.bbugc__slide')
    );
    var i = slides.indexOf(e.target.closest('.bbugc__slide'));
    if (i > -1) inst.scrollTileIntoView(i);
  });
})();
